import { type FileDefinitionValue } from 'app/core/files/types';
import { type EconomyActivity } from 'app/modules/Operations/EconomyActivity/models/economyActivity.model';
import { type ContactModel, type TaxStep } from 'app/modules/Taxpayer/TaxpayerRequest/steps/types';
import {
  type GeneralTaxpayer,
  type ReportRow,
  type Steps,
  type TaxRow
} from 'app/modules/Taxpayer/TaxpayerRequest/types';
import { create } from 'zustand';

interface TaxpayerRequestStore {
  form: Steps;
  contact: ContactModel | {};
  sending: boolean;
  setRequest: (steps: Steps) => void;
  addGeneralData: (data: GeneralTaxpayer) => void;
  addOperationsData: (data: Array<EconomyActivity>) => void;
  addDocumentsData: (data: Array<FileDefinitionValue>) => void;
  addContact: (contact: ContactModel) => void;
  removeContact: (contact: ContactModel) => void;
  setContact: (contact: ContactModel) => void;
  addTax: (tax: TaxStep) => void;
  addTaxPeriodicities: (taxes: Array<TaxRow>) => void;
  addReportPeriodicities: (reports: Array<ReportRow>) => void;
  setSending: (sending: boolean) => void;
  reset: () => void;
}

const initialForm: Steps = {
  contacts: [],
  general: {},
  tax: {} as TaxStep,
  taxes: [],
  reports: [],
  operations: [],
  documents: []
};

export const useTaxpayerRequestStore = create<TaxpayerRequestStore>()((set) => ({
  form: initialForm,
  contact: {},
  sending: false,
  setRequest: (steps) => set({ form: steps }),
  addGeneralData: (data) => set((state) => ({ form: { ...state.form, general: data } })),
  addOperationsData: (data) => set((state) => ({ form: { ...state.form, operations: data } })),
  addDocumentsData: (data) => set((state) => ({ form: { ...state.form, documents: data } })),
  addContact: (contact) =>
    set((state) => {
      const contacts = state.form.contacts ?? [];
      const exists = contacts.find((c) => c.index === contact.index);
      const updated = exists
        ? contacts.map((c) => (c.index === contact.index ? contact : c))
        : [...contacts, contact];
      return { form: { ...state.form, contacts: updated } };
    }),
  removeContact: (contact) =>
    set((state) => ({
      form: {
        ...state.form,
        contacts: state.form.contacts?.filter((c) => c.index !== contact.index)
      },
      contact: {}
    })),
  setContact: (contact) => set({ contact }),
  addTax: (tax) => set((state) => ({ form: { ...state.form, tax } })),
  addTaxPeriodicities: (taxes) => set((state) => ({ form: { ...state.form, taxes: [...taxes] } })),
  addReportPeriodicities: (reports) =>
    set((state) => ({ form: { ...state.form, reports: [...reports] } })),
  setSending: (sending) => set({ sending }),
  reset: () => set({ form: initialForm, contact: {}, sending: false })
}));
